import { useEffect, useRef, useState } from "react";
import { useReveal } from "@/lib/useReveal";

const stats = [
  { value: 3, suffix: "", label: "Active Product Pillars" },
  { value: 100, suffix: "%", label: "Evidence-Based Design" },
  { value: 24, suffix: "/7", label: "Compliance Posture" },
  { value: 1, suffix: "·India", label: "Region One Focus" },
];

function useCount(target, active) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const duration = 1600;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target]);
  return n;
}

export default function WhoWeAre() {
  const ref = useReveal();
  const [statsActive, setStatsActive] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    if (!statsRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setStatsActive(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(statsRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="who"
      data-testid="who-section"
      ref={ref}
      className="relative overflow-hidden bg-[#0F1B2D] text-white py-28 md:py-40 lg:py-48"
    >
      {/* Background video (FOR BLUDE SITE) with dark mask */}
      <div className="absolute inset-0 -z-0 opacity-[0.35]">
        <video
          data-testid="who-video"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src={`${process.env.PUBLIC_URL}/videos/whoweare.mp4`}
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-[#0F1B2D]/65" />
      </div>

      {/* Red soft glow */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-[#D62828]/20 blur-[120px]" />
      <div className="pointer-events-none absolute right-0 bottom-0 h-[520px] w-[520px] rounded-full bg-[#A4161A]/10 blur-[140px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-white/60 reveal">
          <span className="inline-block h-px w-10 bg-[#D62828]" />
          Who We Are
        </div>

        <div className="mt-8 grid grid-cols-12 gap-8 lg:gap-12">
          <h2
            data-testid="who-headline"
            className="col-span-12 lg:col-span-8 font-display text-[clamp(2.5rem,5.5vw,5.5rem)] leading-[0.95] tracking-[-0.035em] reveal"
          >
            Science-driven solutions for a{" "}
            <span className="font-serif-italic text-[#D62828] italic">
              healthier
            </span>{" "}
            world.
          </h2>

          <div className="col-span-12 lg:col-span-4 lg:pt-6 text-[15px] leading-[1.7] text-white/70 reveal">
            <p>
              Blude TechX is a healthcare technology startup focused on improving
              accessibility, communication, and efficiency through digital
              innovation.
            </p>
            <p className="mt-5">
              We bridge patients, doctors, hospitals, and healthcare systems
              using validated technology designed for real-world impact.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div
          ref={statsRef}
          data-testid="who-stats"
          className="mt-20 md:mt-28 grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 border-t border-white/10 pt-12"
        >
          {stats.map((s, i) => (
            <Stat key={i} {...s} active={statsActive} />
          ))}
        </div>

        {/* Feature cards */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "Validated Outcomes",
              body: "Each product is shaped by clinical insight, data discipline, and measurable patient outcomes.",
            },
            {
              title: "Engineered for Trust",
              body: "Privacy-first architectures, audit trails, and compliance built into the core, not bolted on.",
            },
            {
              title: "Built for India",
              body: "Calibrated to the realities of clinics, hospitals, and healthcare ecosystems across South India and beyond.",
            },
          ].map((f, i) => (
            <div
              key={i}
              data-testid={`who-feature-${i}`}
              className="reveal group relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-7 hover:bg-white/[0.06] transition-colors duration-500"
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/50">
                <span>0{i + 1}</span>
                <span className="h-1.5 w-1.5 rounded-full bg-[#D62828]" />
              </div>
              <h3 className="mt-6 font-display text-2xl tracking-tight text-white">
                {f.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-white/65">
                {f.body}
              </p>
              <div className="mt-8 h-px bg-white/10 group-hover:bg-[#D62828]/50 transition-colors duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ value, suffix, label, active }) {
  const isNumber = typeof value === "number";
  const count = useCount(isNumber ? value : 0, active);
  const display = isNumber ? count : value;
  return (
    <div data-testid={`stat-${label}`} className="reveal">
      <div className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter">
        {display}
        <span className="text-[#D62828]">{suffix}</span>
      </div>
      <div className="mt-3 text-[11px] uppercase tracking-[0.24em] text-white/55">
        {label}
      </div>
    </div>
  );
}
